import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import * as Icons from "lucide-react";
import { Reveal, SectionLabel } from "../components/Reveal";
import HeroVideo from "../components/HeroVideo";
import BrandMarquee from "../components/BrandMarquee";
import { IMG, STATS } from "../data/content";
import { useProjects } from "../hooks/useProjects";
import { useSiteServices } from "../hooks/useSiteServices";

export default function Home() {
  const { projects } = useProjects();
  const { home_cards: services, packages } = useSiteServices();
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <HeroVideo />
        <div className="absolute inset-0 bg-background/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-background/20 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-24">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl">
            <SectionLabel>Interiors · Construction · Hyderabad</SectionLabel>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6 tracking-tight">
              Spaces that fulfil<br />
              <span className="italic text-accent">every wish.</span>
            </h1>
            <p className="mt-7 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Kalpavriksha crafts divine, grounded and timeless interiors — from the first sketch to
              the final handover. Premium materials, master artisans, and transparent pricing.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-cta-button" to="/calculator" className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-medium hover:bg-accent hover:text-accent-foreground transition-colors duration-300">
                Estimate Your Project <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link data-testid="hero-projects-button" to="/projects" className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-7 py-3.5 font-medium hover:border-accent hover:text-accent transition-colors duration-300">
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center md:text-left">
              <p className="font-serif-display text-4xl md:text-5xl text-accent">{s.value}</p>
              <p className="label-caps text-muted-foreground mt-2">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <BrandMarquee />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <div className="relative">
            <img src={IMG.living2} alt="Kalpavriksha interior" className="rounded-3xl w-full object-cover aspect-[4/5]" />
            <div className="absolute -bottom-8 -right-4 md:right-8 glass rounded-2xl p-6 max-w-[200px]">
              <p className="font-serif-display text-3xl text-accent">₹1,399</p>
              <p className="text-sm text-muted-foreground">starting per sq.ft, all-inclusive</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <SectionLabel>Who We Are</SectionLabel>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-5 leading-tight">
            A studio rooted in craft, grown for the modern home.
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Named after the mythical wish-fulfilling tree, Kalpavriksha Interiors & Constructions has
            delivered 250+ residential and commercial spaces across Telangana. We blend natural
            materials, precise engineering and honest pricing under one roof.
          </p>
          <ul className="mt-8 space-y-4">
            {["End-to-end design & build", "Transparent, package-based pricing", "Premium brands: Hafele, Hettich, CenturyPly"].map((f) => (
              <li key={f} className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-accent" /> <span>{f}</span>
              </li>
            ))}
          </ul>
          <Link to="/about" className="inline-flex items-center gap-2 mt-9 font-medium text-accent hover:gap-3 transition-all">
            More about our story <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>

      {/* SERVICES */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal><SectionLabel>What We Do</SectionLabel></Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-5 mb-14">
            <h2 className="font-serif-display text-4xl md:text-5xl max-w-xl leading-tight">Services crafted around your life</h2>
            <Link to="/services" className="inline-flex items-center gap-2 text-accent font-medium">All services <ArrowUpRight size={18} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              return (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="hover-lift h-full rounded-2xl bg-card border border-border p-8">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-5">
                    <Icon size={22} />
                  </span>
                  <h3 className="font-serif-display text-2xl">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );})}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal><SectionLabel>Selected Work</SectionLabel></Reveal>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-5 mb-14">Recent transformations</h2>
        <div className="grid lg:grid-cols-12 gap-6">
          {projects.slice(0, 3).map((p, i) => (
            <Reveal key={p.id || p.title} delay={i * 0.08} className={p.span}>
              <Link to="/projects" className="group block relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute bottom-0 p-6 text-white">
                  <p className="label-caps text-accent">{p.area ? `${p.cat} · ${p.area}` : p.cat}</p>
                  <h3 className="font-serif-display text-2xl mt-1">{p.title}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PACKAGES CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <SectionLabel>Transparent Pricing</SectionLabel>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-5 leading-tight">Four packages. Zero surprises.</h2>
              <p className="mt-6 text-primary-foreground/70 leading-relaxed max-w-md">
                Every package is charged per square foot and includes design, materials and labour.
                Use our live calculator to see your number instantly.
              </p>
              <Link data-testid="packages-cta" to="/calculator" className="inline-flex items-center gap-2 mt-9 rounded-full bg-accent text-accent-foreground px-7 py-3.5 font-medium hover:opacity-90 transition-opacity">
                Open Calculator <ArrowRight size={18} />
              </Link>
            </Reveal>
            <div className="grid grid-cols-2 gap-4">
              {packages.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.07}>
                  <div className="rounded-2xl border border-primary-foreground/15 p-6 hover:border-accent transition-colors">
                    <p className="label-caps text-accent">{p.tag}</p>
                    <p className="font-serif-display text-3xl mt-2 flex items-baseline gap-2" data-testid={`home-pkg-price-${p.name.toLowerCase()}`}>
                      {p.oldRate && <span className="text-lg line-through opacity-40">₹{p.oldRate.toLocaleString("en-IN")}</span>}
                      <span>₹{p.rate.toLocaleString("en-IN")}</span>
                    </p>
                    <p className="text-sm text-primary-foreground/60">/ sq.ft — {p.name}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
